import { useState } from 'react'
import { Star } from 'lucide-react'
import type { NtCourse, NtTerm, NtVisit } from '../../core/types'
import { useVisits, assignVisit, unassignVisit, logVisit, suggestVisits, useNorseTermTeachers, DAY_LABELS, SESSION_LABELS } from './model'
import { Area } from '../../ui/Field'
import { Empty, ErrorState } from '../../ui/States'

/**
 * Site visits — an administrator dropping in on a course during the week.
 *
 * The point is coverage: every course gets seen once before any course gets
 * seen twice. That is easy to get wrong by hand across thirty courses and four
 * days, so the suggestion does the arithmetic and the coordinator edits it —
 * the algorithm does not know the principal has a board meeting Tuesday.
 *
 * The visitor's note is theirs and nobody else can write it. This is
 * deliberately **not** an evaluation instrument: a rating here is "what I saw
 * in twenty minutes", and the moment it feeds anything formal it belongs in a
 * negotiated procedure instead of a web app.
 */
export function Visits({ term, courses, canAssign, meEmail }: {
  term: NtTerm
  courses: NtCourse[]
  canAssign: boolean
  meEmail: string
}) {
  const visits = useVisits(term.id)
  // Visitors are the Norse Term teachers: the one slice of /users that
  // whoever assigns visits may list (the whole list needs admin.grant, and a
  // refused listener wedges the Firestore cache).
  const teachers = useNorseTermTeachers(canAssign)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const live = courses.filter((c) => c.status === 'approved' && c.block)
  const byCourse = new Map(courses.map((c) => [c.id, c]))
  const mine = visits.filter((v) => v.visitorEmail === meEmail)
  const uncovered = live.filter((c) => !visits.some((v) => v.courseId === c.id))

  const visitors = teachers

  const suggest = async () => {
    setBusy(true); setErr(null)
    try {
      const plan = suggestVisits(courses, visitors, visits)
      for (const v of plan) await assignVisit(v)
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  return (
    <>
      {err && <ErrorState body={err} />}

      {mine.length > 0 && (
        <>
          <h2 className="work__heading">Yours <span className="work__badge">{mine.length}</span></h2>
          {mine.map((v) => (
            <VisitCard key={v.id} v={v} title={byCourse.get(v.courseId)?.title ?? v.courseId} />
          ))}
        </>
      )}

      {canAssign && (
        <>
          <h2 className="work__heading">Coverage</h2>
          <div className="card">
            <div className="dl">
              <div><div className="dl__k">Courses</div><div className="dl__v">{live.length}</div></div>
              <div><div className="dl__k">Assigned</div><div className="dl__v">{live.length - uncovered.length}</div></div>
              <div><div className="dl__k">Logged</div>
                <div className="dl__v">{visits.filter((v) => v.loggedAt).length}</div></div>
            </div>
            {uncovered.length > 0 && (
              <p className="opt__hint">
                <strong>Nobody assigned to:</strong> {uncovered.map((c) => c.title).join(', ')}
              </p>
            )}
            <div className="opt__bar">
              <button className="btn" onClick={suggest} disabled={busy || !uncovered.length || !visitors.length}>
                {busy ? 'Assigning…' : `Fill the ${uncovered.length} gap${uncovered.length === 1 ? '' : 's'}`}
              </button>
            </div>
            <p className="opt__hint">
              Spreads visits so every course is seen once before any is seen twice, and nobody is
              in two rooms at the same time. Edit anything that does not suit; it is a suggestion.
            </p>
          </div>

          <h2 className="work__heading">Everything assigned</h2>
          {visits.length === 0
            ? <Empty title="No visits yet" body="Assign them above, or add them one at a time." />
            : (
              <ul className="work">
                {visits.map((v) => (
                  <li key={v.id} className="work__row">
                    <div className="work__main">
                      <div style={{ fontWeight: 610 }}>{byCourse.get(v.courseId)?.title ?? v.courseId}</div>
                      <div className="work__sub">
                        {DAY_LABELS[v.day]} {SESSION_LABELS[v.session].toLowerCase()} · {v.visitorName}
                      </div>
                      {v.note && <div className="work__status">“{v.note}”</div>}
                    </div>
                    {v.loggedAt
                      ? <span className="pill pill--good">Logged</span>
                      : <button className="opt__icon opt__icon--danger" aria-label="Remove this visit"
                          onClick={() => unassignVisit(v.id)}>✕</button>}
                  </li>
                ))}
              </ul>
            )}
        </>
      )}

      {!canAssign && mine.length === 0 && (
        <Empty title="No visits assigned to you" body="The office assigns these during the week." />
      )}
    </>
  )
}

function VisitCard({ v, title }: { v: NtVisit; title: string }) {
  const [note, setNote] = useState(v.note ?? '')
  const [rating, setRating] = useState(v.rating ?? 0)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    setBusy(true); setErr(null)
    try { await logVisit(v.id, note.trim(), rating) }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  return (
    <section className="card card--action">
      <h3 style={{ fontSize: 'var(--t-md)', margin: 0 }}>{title}</h3>
      <p style={{ color: 'var(--ink-muted)', fontSize: 'var(--t-sm)', margin: '2px 0 var(--s-3)' }}>
        {DAY_LABELS[v.day]} · {SESSION_LABELS[v.session].toLowerCase()}
      </p>
      {err && <ErrorState body={err} />}

      <div className="chips" style={{ marginBottom: 'var(--s-3)' }}>
        {[1, 2, 3, 4].map((n) => (
          <button key={n} className={`chip${rating === n ? ' is-on' : ''}`} onClick={() => setRating(n)}>
            <Star size={13} aria-hidden="true" /> {n}
          </button>
        ))}
      </div>

      <Area label="What you saw" value={note} onChange={setNote}
        placeholder="Twelve kids, all with something in their hands. Two were stuck and asked each other before asking him." />
      <button className="btn" onClick={save} disabled={busy}>
        {busy ? 'Saving…' : v.loggedAt ? 'Update' : 'Log the visit'}
      </button>
      <p className="opt__hint">
        Your note, and only yours. This is not an evaluation. It is twenty minutes, written down.
      </p>
    </section>
  )
}
